package nl.volleyplanner.web.controller.thuisprogramma

import kotlinx.coroutines.flow.StateFlow
import kotlinx.html.TagConsumer
import kotlinx.html.dd
import kotlinx.html.dl
import kotlinx.html.dt
import kotlinx.html.js.div
import kotlinx.html.span
import kotlinx.serialization.json.Json
import nl.volleyplanner.api.backend_jobs.JobStatus
import nl.volleyplanner.api.backend_jobs.PlannerJobDetails
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.component.StateListenerContentComponent
import nl.volleyplanner.web.base.component.tooltipAttributes
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.component.backend_jobs.JobFlowState
import nl.volleyplanner.web.component.scoreBadge
import nl.volleyplanner.web.lib.Tooltip
import nl.volleyplanner.web.util.BootstrapColor
import org.w3c.dom.HTMLElement
import org.w3c.dom.get

class PlannerJobProgressTrackerComponent(
    private val jobTrackerState: StateFlow<JobFlowState>
) : HTMLComponent() {

    override fun onContentReplaced(htmlElement: HTMLElement) {
        super.onContentReplaced(htmlElement)
        createTooltips(htmlElement)
    }

    private fun createTooltips(htmlElement: HTMLElement) {
        val tooltipElements = htmlElement.querySelectorAll("[data-bs-toggle=\"tooltip\"]")
        if (tooltipElements != null) {
            for (i in 0 until tooltipElements.length) {
                val tooltipElement = tooltipElements[i] as HTMLElement
                Tooltip.Companion.getOrCreateInstance(tooltipElement)
            }
        }
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div(classes = "my-2 p-2 border border-secondary") {
            dl(classes = "row") {
                dt(classes = "col-4") { +"Status" }
                component(StateListenerContentComponent<JobFlowState>(jobTrackerState) { state ->
                    val latestJobExecution = jobTrackerState.value.jobExecution
                    dd(classes = "col-8") {
                        if (latestJobExecution != null && latestJobExecution.status != JobStatus.Finished) {
                            spinner(small = true)
                        }
                        +" ${latestJobExecution?.status?.name ?: "-"}"
                    }
                })
                dt (classes = "col-4") { +"Resultaat" }
                component(StateListenerContentComponent<JobFlowState>(jobTrackerState) { state ->
                    val latestJobExecution = jobTrackerState.value.jobExecution
                    dd(classes = "col-8") { +(latestJobExecution?.result?.name ?: "-") }
                })
                dt (classes = "col-4") {
                    +"Aantal berekeningen"
                    span {
                        tooltipAttributes(
                            titleLabel = "Statistiek voor nerds. Toont het aantal combinaties dat de planner heeft geprobeerd en uitgerekend<br>" +
                                    "Er zijn miljarden combinaties mogelijk. Met behulp van slimme algoritmes kan de planner met slechts een fractie " +
                                    "tot een optimale oplossing komen."
                        )
                        icon("bi-info-circle", color = BootstrapColor.Secondary)
                    }
                }
                component(StateListenerContentComponent<JobFlowState>(jobTrackerState) { state ->
                    val latestJobExecution = jobTrackerState.value.jobExecution
                    val plannerJobDetails = latestJobExecution?.backendJobExecution?.details?.let {
                        Json.Default.decodeFromString<PlannerJobDetails>(it)
                    }
                    dd(classes = "col-8") { +(plannerJobDetails?.totalCalculations?.toString() ?: "-") }
                })
                dt (classes = "col-4") {
                    +"Score"
                    span {
                        tooltipAttributes(
                            titleLabel = "De beste totaalscore (alle taken opgeteld) die de planner tot nog toe heeft gevonden. <br>" +
                                    "De planner zoekt naar de minst negatieve score als meest optimale scenario voor jullie puzzel."
                        )
                        icon("bi-info-circle", color = BootstrapColor.Secondary)
                    }
                }
                component(StateListenerContentComponent<JobFlowState>(jobTrackerState) { state ->
                    val latestJobExecution = jobTrackerState.value.jobExecution
                    val plannerJobDetails = latestJobExecution?.backendJobExecution?.details?.let {
                        Json.Default.decodeFromString<PlannerJobDetails>(it)
                    }
                    dd(classes = "col-8") {
                        if (plannerJobDetails?.lastBestScore != null) {
                            scoreBadge(plannerJobDetails.lastBestScore)
                        } else {
                            +"-"
                        }
                    }
                })
            }
        }
    }

    override suspend fun getState() = null
}